import { FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import OrderDetailCard from '@/components/feature/OrderDetailCard';
import { useMemberAuth } from '@/contexts/MemberAuthContext';
import { Order, lookupOrdersByContact, orderStatusLabels, paymentStatusLabels } from '@/services/orders';

const inputClass =
  'w-full px-3.5 py-2.5 rounded-xl border border-[#E8E0D6] text-base outline-none focus:ring-2 focus:ring-[#1B3D2A]/15 focus:border-[#1B3D2A]';

const statusBadgeClass: Record<string, string> = {
  Pending: 'bg-[#FAF3E4] text-[#B5791F]',
  Processing: 'bg-[#E9F1FB] text-[#2A5CA8]',
  Shipped: 'bg-[#EFF7F1] text-[#1B3D2A]',
  Completed: 'bg-[#1B3D2A]/10 text-[#1B3D2A]',
  Cancelled: 'bg-[#fdebea] text-[#c3423f]',
};

export default function OrderLookup() {
  const navigate = useNavigate();
  const { isLoggedIn } = useMemberAuth();
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [orders, setOrders] = useState<Order[]>([]);
  const [expandedOrderNumber, setExpandedOrderNumber] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setLoading(true);
    setError('');
    setOrders([]);
    setExpandedOrderNumber(null);
    setSearched(true);
    try {
      const data = await lookupOrdersByContact(email.trim(), phone.trim());
      setOrders(data.Orders);
    } catch (err) {
      setError(err instanceof Error ? err.message : '查詢失敗，請確認 Email 與聯絡電話是否正確。');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-20 md:pt-24">
        <div className="border-b border-[#E8E0D6] bg-[#FAF8F5] px-6 py-8 md:px-10 md:py-12 lg:px-16">
          <div className="mx-auto max-w-3xl">
            <span className="inline-block text-xs font-semibold tracking-widest text-[#D4A574] uppercase mb-2">Order Lookup</span>
            <h1 className="text-2xl font-bold text-[#1B3D2A] md:text-3xl">訂單查詢</h1>
            <p className="mt-2 text-sm text-[#5A6B5A]">
              請輸入訂購時填寫的 Email 與聯絡電話，即可查詢您所有的訂單，不需要記住訂單編號。
              {!isLoggedIn && (
                <>
                  {' '}若您是會員，
                  <button type="button" onClick={() => navigate('/login?redirect=/account/orders')} className="font-bold text-[#1B3D2A] hover:underline">
                    登入後可直接查看所有訂單
                  </button>
                  。
                </>
              )}
            </p>
          </div>
        </div>

        <div className="mx-auto max-w-3xl px-6 py-8 md:px-10 md:py-12 lg:px-16">
          <form onSubmit={handleSubmit} className="grid gap-4 rounded-2xl border border-[#E8E0D6] bg-[#FAF8F5] p-6 md:grid-cols-2">
            <div>
              <label htmlFor="lookup-email" className="block text-sm font-medium text-[#1B3D2A] mb-1.5">訂購 Email</label>
              <input
                id="lookup-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@example.com"
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="lookup-phone" className="block text-sm font-medium text-[#1B3D2A] mb-1.5">訂購聯絡電話</label>
              <input
                id="lookup-phone"
                required
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="0912345678"
                className={inputClass}
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="md:col-span-2 rounded-xl bg-[#1B3D2A] py-3.5 text-base font-bold text-white hover:bg-[#143026] disabled:opacity-60"
            >
              {loading ? '查詢中...' : '查詢訂單'}
            </button>
          </form>

          {searched && !loading && (
            <div className="mt-8">
              {error ? (
                <div className="rounded-2xl border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm text-[#c3423f]">{error}</div>
              ) : orders.length === 0 ? (
                <div className="rounded-2xl border border-[#E8E0D6] bg-[#FAF8F5] px-4 py-8 text-center text-sm text-[#9A8A7A]">
                  查無符合的訂單，請確認 Email 與電話是否與訂購時填寫的資料一致。
                </div>
              ) : (
                <div className="grid gap-4">
                  {orders.map((order) => {
                    const expanded = expandedOrderNumber === order.OrderNumber;
                    return (
                      <div key={order.OrderId} className="rounded-2xl border border-[#E8E0D6] bg-white">
                        <button
                          type="button"
                          onClick={() => setExpandedOrderNumber(expanded ? null : order.OrderNumber)}
                          className="flex w-full flex-wrap items-center justify-between gap-2 p-5 text-left"
                        >
                          <div>
                            <p className="text-sm font-bold text-[#1B3D2A]">訂單編號 {order.OrderNumber}</p>
                            <p className="mt-0.5 text-xs text-[#9A8A7A]">{order.CreatedAt}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`rounded-full px-3 py-1 text-xs font-bold ${statusBadgeClass[order.OrderStatus] || ''}`}>
                              {orderStatusLabels[order.OrderStatus]}
                            </span>
                            <span className="rounded-full bg-[#FAF8F5] px-3 py-1 text-xs font-bold text-[#5A6B5A]">
                              {paymentStatusLabels[order.PaymentStatus]}
                            </span>
                            <i className={`ri-arrow-down-s-line text-xl text-[#9A8A7A] transition-transform ${expanded ? 'rotate-180' : ''}`} />
                          </div>
                        </button>
                        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-[#E8E0D6] px-5 py-3">
                          <p className="text-sm text-[#5A6B5A]">共 {order.Items.reduce((sum, item) => sum + item.Quantity, 0)} 件商品</p>
                          <p className="text-base font-bold text-[#1B3D2A]">NT$ {order.TotalAmount.toLocaleString()}</p>
                        </div>
                        {expanded && (
                          <div className="border-t border-[#E8E0D6] p-5">
                            <OrderDetailCard order={order} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
